import {CloudObject, registerBrick, File as OFile, Transaction, Config} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import React, {useRef, useState} from 'react';
import Button from '@mui/material/Button';
import {orangeTheme} from './_theme';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Portal,
    Snackbar,
    ThemeProvider, Tooltip,
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LaunchIcon from '@mui/icons-material/Launch';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {LoadingButton} from '@mui/lab';
import {ErrorPopup} from './pr_editor/dialogs/ErrorPopup.jsx';
import {downloadToExcelLambda, getExcelToPRLambda, sendEmailLambda} from '../utils/functionSignatures';
import {ezprOrange} from './_helpers';
import {MassCreationInvalidPrsPopup} from './pr_editor/dialogs/MassCreationInvalidPrsPopup.jsx';
import * as ExcelJS from 'exceljs';
import {PurchaseRequest} from '../models/PurchaseRequest';
import {getMailContentMassCreationPRS, validateEmail} from '../utils/email';
import {mergeUniqueValuesWithKey} from '../utils/array';
import {excelHeaderFields} from '../utils/constants';
import {itemsExportPR} from '../utils/itemsExportPR';

/**
 * This brick is displaying 1 or 2 options to create a new request, based on the user default company code
 */
export default class UploadExcelToPR extends ReactBrick {
    /**
     * @override
     */
    static getReactComponent($) {
        return () => {
            const [showUploadDialog, setShowUploadDialog] = useState(false);
            const inputRef = useRef();
            const fileName = useRef('');
            const [uploadedFile, setUploadedFile] = useState('');
            const [snackbar, setSnackbar] = useState(null);
            const [uploadExcelLoading, setUploadExcelLoading] = useState(false);
            const [errorMsg, setErrorMsg] = useState('');
            const [showInvalidPrsPopup, setShowInvalidPrsPopup] = useState(false);
            const invalidPrs = useRef([]);
            const downloadToExcelLoading = useProperty($, 'Download to Excel loading');

            const resetInput = () => {
                fileName.current = '';
                setUploadExcelLoading(false);
                setUploadedFile(null);
            };

            const handleClick = () => {
                setShowUploadDialog(true);
            };

            const handleClose = () => {
                resetInput();
                setShowUploadDialog(false);
            };

            const handleUploadFile = (event) => {
                if (event.target.files.length > 0) {
                    const file = event.target.files[0];
                    const extension = file.name.split('.').pop().toLowerCase();
                    const allowed = ['xls', 'xlsx'];

                    if (allowed.indexOf(extension) === -1) {
                        alert(`Please upload a file that has the xls or xlsx extension.`);
                        resetInput();
                        return;
                    }
                    const selectableMaxFileSize = 1024 * 1024;
                    const fileSize = file.size;

                    // Do not allow users to upload files higher size than 1 MB
                    if (fileSize > selectableMaxFileSize) {
                        alert(`Please upload a file smaller than 1 MB.`);
                        resetInput();
                        return;
                    }
                    fileName.current = file.name;
                    setUploadedFile(file);
                } else {
                    fileName.current = '';
                    setUploadedFile(null);
                }
            };

            const handleUpload = async () => {
                const handleSuccess = () => {
                    setUploadExcelLoading(false);
                    setSnackbar({
                        children: `The file has been successfully uploaded. You will receive an email with all corresponding details in a few minutes.`,
                        severity: 'success',
                    });
                    setShowInvalidPrsPopup(invalidPrs.current.length > 0);
                    setShowUploadDialog(false);

                    resetInput();
                };

                const handleSnackbarError = (message = '', severity = 'error') => {
                    let snackBarMsg;

                    if (typeof message === 'object' && message !== null) {
                        snackBarMsg = message.message || '';
                    } else if (typeof message === 'string') {
                        snackBarMsg = message;
                    }

                    if (snackBarMsg) {
                        snackBarMsg = `Something went wrong with mass creation: ${snackBarMsg}`;
                    } else {
                        snackBarMsg = 'Something went wrong with mass creation.';
                    }

                    if (!navigator.onLine) {
                        snackBarMsg = 'Network error: You appear to be offline. Please check your internet connection.';
                    } else if (
                        snackBarMsg.includes('Failed to fetch') ||
                        snackBarMsg.includes('ERR_INTERNET_DISCONNECTED') ||
                        snackBarMsg.includes('NetworkError') ||
                        snackBarMsg.includes('Connection refused')
                    ) {
                        snackBarMsg = 'Network error: Your request could not be processed. Please try again when you have a stable connection.';
                    }

                    if (!snackBarMsg) {
                        snackBarMsg = 'The file could not be uploaded. Try again or use the file template the system provides you to respect the expected format.';
                    }

                    setSnackbar({
                        children: snackBarMsg,
                        severity: severity,
                    });
                    handleClose();
                };

                const sendMassCreationEmail = (prList, invalidPrList, notCreatedPrList, validPrStatusRsp) => {
                    const fileTag = $.get('Template File Tag');
                    CloudObject.get(fileTag).getContentAsBinary(
                        async (binary) => {
                            try {
                                const workbook = new ExcelJS.Workbook();

                                await workbook.xlsx.load(binary, {useStyles: true});

                                const headerSheet = workbook.getWorksheet('Header');
                                const itemsSheet = workbook.getWorksheet('Items');

                                // Header sheet data
                                const uniquePRs = new Map();
                                const spenderEmail = [];
                                const requesterEmail = [];
                                const otherEmail = [];
                                if ( prList) {
                                    prList?.forEach((jsonString) => {
                                        const convertedPr = new PurchaseRequest(jsonString);
                                        const EPRId = 'EPR-' + convertedPr.EPRId;

                                        if (convertedPr.spender?.email && validateEmail(convertedPr.spender.email)) {
                                            spenderEmail.push(convertedPr.spender.email);
                                        } else {
                                            console.warn(`Invalid or missing spender email in ${EPRId}`);
                                        }

                                        if (convertedPr.requester?.email && validateEmail(convertedPr.requester.email)) {
                                            requesterEmail.push(convertedPr.requester.email);
                                        } else {
                                            console.warn(`Invalid or missing requester email in ${EPRId}`);
                                        }

                                        if (convertedPr.sendingOptions?.otherAddress && validateEmail(convertedPr.sendingOptions.otherAddress)) {
                                            otherEmail.push(convertedPr.sendingOptions.otherAddress);
                                        } else {
                                            console.warn(`Invalid or missing other address email in ${EPRId}`);
                                        }

                                        if (!uniquePRs.has(EPRId)) {
                                            uniquePRs.set(EPRId, convertedPr);
                                        }
                                    });
                                }

                                if (notCreatedPrList && localStorage.getItem('userEmail')) {
                                    requesterEmail.push(localStorage.getItem('userEmail'));
                                }

                                let rowNumber = 3;
                                uniquePRs.forEach((prData, EPRId) => {
                                    const row = headerSheet.getRow(rowNumber);
                                    excelHeaderFields.forEach((field) => {
                                        const cell = row.getCell(field.column);
                                        let value = field.value(prData, EPRId);

                                        if (value === null || value === undefined) {
                                            value = '';
                                        } else {
                                            value = value.toString();
                                        }

                                        cell.value = value;
                                    });
                                    row.commit();
                                    rowNumber++;
                                });

                                // items sheet part
                                rowNumber = 3;
                                uniquePRs.forEach((prData, EPRId)=>{
                                    const itemsData = itemsExportPR(prData, EPRId);
                                    itemsData.forEach((rowData) => {
                                        const row = itemsSheet.getRow(rowNumber);
                                        rowData.forEach((value, colIndex) => {
                                            row.getCell(colIndex + 1).value = value;
                                        });
                                        row.commit();
                                        rowNumber++;
                                    });
                                });

                                const modifiedBinary = await workbook.xlsx.writeBuffer();


                                const mailAddress= Array.from(new Set(requesterEmail)).join(';');
                                const ccMailAddress = Array.from(new Set(spenderEmail)).join(';');

                                const transaction = new Transaction(false);
                                const fileTag = transaction.create(OFile);
                                transaction.persist(fileTag, true);
                                const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                                OFile.setContent(transaction, fileTag, 'MassUploadPR.xlsx', modifiedBinary, mimeType);
                                transaction
                                    .execute()
                                    .then(() => sendEmail(fileTag, mailAddress, ccMailAddress, validPrStatusRsp, invalidPrList, notCreatedPrList))
                                    .catch((message) => {
                                        handleSnackbarError('The file has been successfully uploaded. Something went wrong with sending the email to the spender/requester..', 'warning');
                                    });
                            } catch (error) {
                                console.error('Error processing the PR list for Mass creation email:', error);
                                handleSnackbarError(error);
                            }
                        },
                        (error) => {
                            console.error('Error fetching binary data for Mass creation email:', error);
                            handleSnackbarError(error);
                        },
                    );
                };

                const sendEmail = (fileTag, mailAddress, ccMailAddress, prList, invalidPrList, notCreatedPrList, warningPrList) => {
                    const env = Config.getParameter('ENV');
                    const attachment = CloudObject.get(fileTag);

                    sendEmailLambda($, $.get('Send Email'), mailAddress, ccMailAddress, '', `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''} Mass PR creation`, getMailContentMassCreationPRS(prList, invalidPrList, notCreatedPrList), [attachment])
                        .then(() => handleSuccess())
                        .catch((msg) => {
                            console.error('Mass creation email notification failed:', msg);
                            handleSuccess();
                        })
                        .finally(() => {
                            const deleteTransaction = new Transaction();
                            deleteTransaction.delete(fileTag).execute().catch((msg) => console.error('Mass creation delete attachment failed:', msg));
                        });
                };

                // Start of handleUpload logic
                setUploadExcelLoading(true);
                const transaction = new Transaction();

                const reader = new FileReader();
                reader.onloadend = () => {
                    const tag = OFile.createFromContent(transaction, uploadedFile.name, reader.result, uploadedFile.type);
                    transaction.persist(tag, false);

                    transaction
                        .execute()
                        .then(() => {
                            getExcelToPRLambda($, $.get('Excel To PR'), tag, (result) => {
                                const [validPrs, invalidPrsRsp, notCreatedPrsRsp, validPrStatusRsp] = result;
                                invalidPrsRsp.forEach((item)=>{
                                    invalidPrs.current = mergeUniqueValuesWithKey(invalidPrs.current, item.invalidPslInformation, 'id' );
                                });
                                validPrs.forEach((item)=> delete item.invalidPslInformation);

                                const validPrsDM = validPrs.map((prDM) => prDM.getJson());

                                if ((validPrsDM && validPrsDM.length > 0) || (notCreatedPrsRsp && notCreatedPrsRsp.length>0)) {
                                    sendMassCreationEmail(validPrsDM, invalidPrs.current, notCreatedPrsRsp, validPrStatusRsp);
                                } else {
                                    setUploadExcelLoading(false);
                                    if (invalidPrs.current.length === 0 && notCreatedPrsRsp.length === 0) {
                                        setShowInvalidPrsPopup(false);
                                        handleSnackbarError('The file could not be uploaded, there is no valid data. Try again or use the file template the system provides you to respect the expected format.');
                                    } else if (invalidPrs.current.length > 0) {
                                        setShowInvalidPrsPopup(true);
                                    }
                                }
                            }, (e) => {
                                console.error('Excel to pr lambda error:  ', e);
                                const msg = e?.message?.includes('PSL material ID') ? e.message : '';
                                handleSnackbarError(msg);
                            });
                        })
                        .catch((message) => {
                            console.error('Creating Excel File error', message);
                            handleSnackbarError(message);
                        });
                };

                reader.readAsArrayBuffer(uploadedFile);
            };

            const handleCloseErrorPopup = () => {
                setErrorMsg('');
            };

            const handleCloseSnackbar = () => setSnackbar(null);

            const handleExampleDownload = () => {
                const fileTag = $.get('Template File Tag');
                CloudObject.get(fileTag).saveAs(`MassPRCreation.xlsx`);
            };

            const handleDownloadToExcel = () => {
                downloadToExcelLambda($, $.get('Download to Excel'))
                    .then(() => console.log('downloaded'))
                    .catch((msg) => {
                        console.error('Download to Excel failed:', msg);
                    });
            };

            return (
                <ThemeProvider theme={orangeTheme}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            verticalAlign: 'middle',
                            border: '1px solid #c7c7c7',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            overflow: 'hidden',
                        }}
                    >
                        <Tooltip title="Import">
                            <Button
                                variant="text"
                                onClick={handleClick}
                                size="small"
                                sx={{
                                    'minWidth': 38,
                                    'height': 38,
                                    'padding': 0,
                                    'color': '#55606a',
                                    'justifyContent': 'center',
                                    'borderRadius': 0,
                                    '&:hover': {
                                        backgroundColor: ezprOrange,
                                        color: '#fff',
                                    },
                                }}
                            >
                                <ExitToAppIcon />
                            </Button>
                        </Tooltip>

                        <Tooltip title="Export">
                            <LoadingButton
                                variant="text"
                                onClick={handleDownloadToExcel}
                                loading={downloadToExcelLoading}
                                size="small"
                                sx={{
                                    'minWidth': 38,
                                    'height': 38,
                                    'padding': 0,
                                    'color': '#55606a',
                                    'justifyContent': 'center',
                                    'borderLeft': '1px solid #c7c7c7',
                                    'borderRadius': 0,
                                    '&:hover': {
                                        backgroundColor: ezprOrange,
                                        color: '#fff',
                                    },
                                }}
                            >
                                <LaunchIcon />
                            </LoadingButton>
                        </Tooltip>
                    </Box>
                    <Dialog open={showUploadDialog} onClose={handleClose} fullWidth>
                        <div className={'pr-popup-header'}>
                            <DialogTitle>Mass PR creation</DialogTitle>
                            <IconButton
                                aria-label="close"
                                onClick={handleClose}
                                sx={{
                                    color: ezprOrange,
                                }}
                            >
                                <CloseIcon
                                    sx={{
                                        width: '50px',
                                        height: '50px',
                                    }}
                                />
                            </IconButton>
                        </div>
                        <DialogContent>
                            <div className={'popup-upload-excel-to-pr-content'}>
                                <div>Upload File: *</div>
                                {fileName.current === '' && (
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        sx={{
                                            'color': '#fff',
                                            'backgroundColor': ezprOrange,
                                            '&:hover': {backgroundColor: '#F2ae49'},
                                            ':disabled': {backgroundColor: 'rgba(0,0,0,0.26)'},
                                        }}
                                    >
                Browse
                                        <input
                                            // Hidden file input
                                            type="file"
                                            multiple={false}
                                            accept=".xlsx"
                                            hidden
                                            ref={inputRef}
                                            // Events
                                            onChange={handleUploadFile}
                                        />
                                    </Button>
                                )}
                                {fileName.current !== '' && (
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <span style={{flexGrow: '4'}}>{fileName.current}</span>
                                        <IconButton
                                            component="span"
                                            size={'small'}
                                            onClick={resetInput}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                )}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                onClick={()=> handleExampleDownload()}
                            >Download Template
                            </Button>
                            <LoadingButton
                                sx={{
                                    'color': '#fff',
                                    'backgroundColor': ezprOrange,
                                    '&:hover': {backgroundColor: '#F2ae49'},
                                    ':disabled': {backgroundColor: 'rgba(0,0,0,0.26)'},
                                }}
                                variant={'outlined'}
                                onClick={handleUpload}
                                disabled={!fileName.current || fileName.current === ''}
                                loading={uploadExcelLoading}
                            >
                                <span>Upload</span>
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                    {errorMsg !== '' && (
                        <ErrorPopup
                            onClose={handleCloseErrorPopup}
                            open={true}
                            title={'Excel Upload Failed'}
                            errorMessages={[errorMsg]}
                        />
                    )}
                    <MassCreationInvalidPrsPopup
                        open={showInvalidPrsPopup}
                        onClose={()=>{
                            setShowInvalidPrsPopup(false);
                        }}
                        invalidPrs={invalidPrs.current}
                    />
                    {!!snackbar && (
                        <Portal>
                            <Snackbar
                                open
                                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                                onClose={handleCloseSnackbar}
                                autoHideDuration={6000}
                            >
                                <Alert {...snackbar} onClose={handleCloseSnackbar} />
                            </Snackbar>
                        </Portal>
                    )}
                </ThemeProvider>
            );
        };
    }
}

registerBrick('01904f6ff0de55c8e7fa', UploadExcelToPR);
